<template>
  <div>
    <v-row v-if="!loading" no-gutters>
      <v-col cols="12">
        <ResourceHorseListHeader :headers="headers" />
        <div style="height:544px;" class="overflow-y-auto d-flex flex-column">
          <div class="d-flex">
            <v-col v-if="canShowField('collaborator')" :cols="canShowField('horse') ? 5 : 12" class="overflow-y-auto">
              <v-row v-for="instructor in sessionObject.instructors" :key="instructor.uuid">
                <ResourceList :content="getInstructorDetail(instructor.uuid)" field="fullname" :session="sessionObject" @deleteContent="handleDelete" />
              </v-row>
            </v-col>
            <v-col
              v-if="canShowField('collaborator') && canShowField('horse')"
              cols="2"
              class="text-center"
            />
            <v-col v-if="canShowField('horse')" :cols="canShowField('collaborator') ? 5 : 12" class="overflow-y-auto">
              <v-row v-for="horse in sessionObject.horses" :key="horse.uuid">
                <ResourceList :content="getHorsedetail(horse.uuid)" field="horse_name" :session="sessionObject" @deleteContent="handleDelete" />
              </v-row>
            </v-col>
          </div>
        </div>
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
      <v-container>
        <v-col cols="12" class="d-inline-block py-0 padding-12">
          <v-col v-if="canShowField('collaborator')" :cols="canShowField('horse') ? 5 : 12" :class="[{'justify-center':!canShowField('horse')}, 'd-inline-block']">
            <ContentPicker
              v-model="selectedInstructors"
              :is-modal-open="isInstructorModalOpen"
              :items="notSelectedInstructors"
              :is-single-select="false"
              wtype="instructor"
              :headers="instructorTableHeaders"
              @modalChangeState="modalChangeState"
              @confirmSelection="confirmSelection"
            >
              <template #trigger>
                <v-btn
                  color="third"
                  outlined
                  large
                  rounded
                  class="font-weight-bold my-3"
                  @click="openSelectedInstructorModal"
                >
                  Ajouter un Instructeur
                  <v-icon size="24" color="third" right>mdi-account-plus</v-icon>
                </v-btn>
              </template>
            </ContentPicker>
          </v-col>
          <v-col v-if="canShowField('collaborator') && canShowField('horse')" cols="2" class="d-inline-block" />
          <v-col v-if="canShowField('horse')" :cols="canShowField('collaborator') ? 5 : 12" :class="[{'justify-center':!canShowField('instructor')}, 'd-inline-block']">
            <ContentPicker
              v-model="selectedHorses"
              :is-modal-open="isHorsesModalOpen"
              :items="notSelectedHorses"
              :is-single-select="false"
              wtype="horse"
              :headers="horsesTableHeaders"
              :is-horse="true"
              @modalChangeState="modalChangeState"
              @confirmSelection="confirmSelection"
            >
              <template #trigger>
                <v-btn
                  color="third"
                  outlined
                  large
                  rounded
                  class="font-weight-bold my-3"
                  @click="openSelectedHorsesModal"
                >
                  Ajouter un cheval
                  <v-icon size="24" color="third" right>mdi-plus</v-icon>
                </v-btn>
              </template>
            </ContentPicker>
          </v-col>
        </v-col>
      </v-container>
    </v-row>
    <div v-else class="d-flex justify-center align-center full-height min-height350">
      <Loader />
    </div>
    <SessionEditOverlay
      v-if="session.recurring && showConfirmationModal"
      :activator.sync="showConfirmationModal"
      :change-confirmation-modal="changeEditSessionOverlay"
      :change-recurring-session="manageContents"
      :options="editOverlayOptions"
      :typeOfAction="typeOfAction"
      :contentType="updateParams.contentType"
    />
  </div>
</template>

<script>
import { ref, watch, provide, computed, onBeforeMount } from '@vue/composition-api';
import SessionEditOverlay from '@/components/sessions/createForm/SessionEditOverlay.vue';
import ResourceHorseListHeader from '../resourceHorseList/ResourceHorseListHeader.vue';
import ResourceList from '../resourceHorseList/ResourceList.vue';
import ContentPicker from '@/components/sessions/ContentPicker.vue';
import { availableHorses, filterHorse } from '@/helpers/dataManager';
import { isObjectEmpty, deleteOptions, stageRadioOptions } from '@/helpers/utils';
import { addInstructorsToSession, addHorsesToSession, deleteContentsFromSession } from '@/helpers/amplify';
import { getSessionFieldByWtype, sessionsWithPeriods, recurringHorseAssignSessions } from '@/helpers/sessions';

import Loader from '@/components/common/Loader.vue';

import store from '@/store';

const dropBySessionType = require('../../configs/dragAndDropLimitations.json');

export default {
  components: { ResourceHorseListHeader, ResourceList, ContentPicker, SessionEditOverlay, Loader },
  props: {
    session: {
      type: Object,
      default: null
    }
  },
  setup (props) {
    const loading = ref(false);
    const instructors = computed(() => store.state.instructors.map(item => ({ uuid: item.uuid, label: item.fullname })));
    const sessionObject = ref(props.session);
    const horses = computed(() => store.state.horses);

    /* Horses filters */
    const selectedLevels = ref([]);
    const horseWithoutRider = ref(null);
    const selectedHorseTypes = ref([]);

    /* Session update variables */
    const typeOfUpdate = ref('SINGLE');
    const typeOfAction = ref(null);
    const updateParams = ref({});
    const deleteParams = ref({});

    /* Modals variables */
    const isInstructorModalOpen = ref(false);
    const isHorsesModalOpen = ref(false);
    const selectedInstructors = ref([]);
    const selectedHorses = ref([]);
    const showConfirmationModal = ref(false);

    /* Modals actions */
    const changeEditSessionOverlay = value => {
      showConfirmationModal.value = value;
    };

    const openSelectedInstructorModal = () => {
      isInstructorModalOpen.value = true;
    };

    const openSelectedHorsesModal = () => {
      isHorsesModalOpen.value = true;
    };

    const editOverlayOptions = computed(() => {
      if (sessionsWithPeriods.includes(props.session.type)) {
        return stageRadioOptions;
      }
      return deleteOptions;
    });

    const canShowField = contentType => {
      if (dropBySessionType[sessionObject.value.type] && !isObjectEmpty(dropBySessionType[sessionObject.value.type])) {
        const limitations = dropBySessionType[sessionObject.value.type].droppableContentType;
        return limitations.includes('*') || limitations.includes(contentType);
      }
      return true;
    };

    const colSize = computed(() => (canShowField('collaborator') && canShowField('horse') ? 6 : 12));

    /* Tables Headers */
    const headers = computed(() => [
      { label: 'Instructeurs', icon: 'mdi-account', canShow: canShowField('collaborator'), cols: colSize.value },
      { isDivider: true, canShow: canShowField('horse'), cols: 1 },
      { label: 'Chevaux', icon: 'mdi-horse', canShow: canShowField('horse'), cols: 5 }
    ]);

    const instructorTableHeaders = [
      { text: 'Nom', value: 'label', sortable: false }
    ];

    const horsesTableHeaders = [
      { text: 'Nom', value: 'label', align: 'center', sortable: false },
      { text: 'Effort quotidien', value: 'dailyEffort', align: 'center', sortable: true },
      { text: 'Effort hebdomadaire', value: 'weeklyEffort', align: 'center', sortable: true }
    ];

    const canAssignHorsesRecurring = computed(() => recurringHorseAssignSessions.includes(props.session.type));

    const getAvailableHorses = computed(() => {
      const availableHorsesForSession = canAssignHorsesRecurring.value ? horses.value : availableHorses(props.session, true, true);

      return filterHorse(availableHorsesForSession, horseWithoutRider.value, props.session, selectedHorseTypes.value, null, selectedLevels.value, true)
        .map(horse => ({ uuid: horse.uuid, wtitle: horse.horse_name, label: horse.horse_name, dailyEffort: horse.dailyEffort, weeklyEffort: horse.weeklyEffort, maxHoursEffort: horse.maxHoursEffort }));
    });

    const notSelectedHorses = computed(() => getAvailableHorses.value.filter(horse => !sessionObject.value.horses.some(h => h.uuid === horse.uuid)));

    const notSelectedInstructors = computed(() => instructors.value.filter(instructor => !sessionObject.value.instructors.some(i => instructor.uuid === i.uuid)));

    const horseAssignRecurringAutoAction = computed(() => store.state.autoActionFlags.horseAssignRecurring);

    const reset = () => {
      selectedInstructors.value = [];
      selectedHorses.value = [];
      typeOfUpdate.value = 'SINGLE';
      typeOfAction.value = null;
      updateParams.value = {};
      deleteParams.value = {};
      isInstructorModalOpen.value = false;
      isHorsesModalOpen.value = false;
      changeEditSessionOverlay(false);
    };

    const addContents = async () => {
      const { contentType, contents } = updateParams.value;

      loading.value = true;

      switch (contentType) {
        case 'instructor':
          await addInstructorsToSession(props.session.id, contents, typeOfUpdate.value);

          break;

        default:
          await addHorsesToSession(props.session.id, contents, typeOfUpdate.value, null, true);
          break;
      }

      loading.value = false;

      /* Close all modals */
      reset();
    };

    const modalChangeState = ({ modalValue, type }) => {
      switch (type) {
        case 'instructor':
          isInstructorModalOpen.value = modalValue;
          break;
        case 'horse':
          isHorsesModalOpen.value = modalValue;
          break;

        default:
          break;
      }
    };

    const deleteContents = async () => {
      const { contentType, uuid } = deleteParams.value;

      loading.value = true;
      await deleteContentsFromSession(props.session, uuid, typeOfUpdate.value, getSessionFieldByWtype(contentType));
      loading.value = false;
    };

    const handleDelete = ({ uuid, contentType }) => {
      deleteParams.value = { contentType, uuid };

      if (props.session.recurring) {
        typeOfAction.value = 'delete';
        changeEditSessionOverlay(true);
        return;
      }

      deleteContents();
    };

    const confirmSelection = async ({ type, selectedItems }) => {
      updateParams.value = { contentType: type, contents: selectedItems };

      const allowedHorsesRecurringUpdate = type === 'horse' ? canAssignHorsesRecurring.value : true;

      if (props.session.recurring && allowedHorsesRecurringUpdate && !horseAssignRecurringAutoAction.value) {
        typeOfAction.value = 'add';
        changeEditSessionOverlay(true);
        return;
      }

      addContents();
    };

    const manageContents = () => {
      changeEditSessionOverlay(false);
      if (typeOfAction.value === 'delete') {
        deleteContents();
        return;
      }
      addContents();
    };

    const getHorsedetail = uuid => store.getters.horse(uuid);

    const getInstructorDetail = uuid => store.getters.instructor(uuid);

    watch(() => props.session, () => {
      sessionObject.value = props.session;
      reset();
    });

    onBeforeMount(async () => {
      loading.value = true;
      if (!store.state.isLazyLoaded.horses) {
        await store.dispatch('getHorses');
      }
      if (!store.state.isLazyLoaded.instructors) {
        await store.dispatch('getInstructors');
      }

      loading.value = false;
    });

    /* Horses Filter */
    provide('horseWithoutParticipant', horseWithoutRider);
    provide('selectedHorseTypes', selectedHorseTypes);
    provide('levelsList', selectedLevels);

    /* Type of session update */
    provide('typeOfupdate', typeOfUpdate);

    return { loading, canShowField, headers, sessionObject, getAvailableHorses, getInstructorDetail, getHorsedetail, instructors, isInstructorModalOpen, isHorsesModalOpen, selectedInstructors, selectedHorses, notSelectedHorses, notSelectedInstructors, openSelectedInstructorModal, openSelectedHorsesModal, instructorTableHeaders, horsesTableHeaders, modalChangeState, editOverlayOptions, confirmSelection, showConfirmationModal, changeEditSessionOverlay, manageContents, handleDelete, typeOfAction, updateParams };
  }
};
</script>
